import { Injectable, Injector } from '@angular/core';
import { User, UserInfo } from '../../entities/User';
import { Credentials } from '../../entities/Credentials';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { BehaviorSubject, catchError, finalize, map, Observable, of, shareReplay, Subject, switchMap, tap, throwError } from 'rxjs';
import { BaseServicesService } from './base.service';
import { Router } from '@angular/router';
import { CambiarPassword } from 'src/app/entities/cambiarPassword';
import { ResetPassword } from './ResetPassword';
import { MonitoreoSocketService } from './monitoreo-socket.service';
import { getIdRolOperativo, getIdRolOperativoString } from 'src/app/shared/utils/jerarquia-operativa.util';

@Injectable({ providedIn: 'root' })
export class AuthenticationService extends BaseServicesService {
  private authenticationChanged = new Subject<boolean>();
  private user = new User();
  private refreshInFlight: Observable<AuthTokens> | null = null;
  private sessionEnding = false;

   private imagenPerfilSubject = new BehaviorSubject<string>(
    this.getUser() && this.getUser().user ? this.getUser().user.imagenPerfil : ''
  );
  
   public imagenPerfil$ = this.imagenPerfilSubject.asObservable();
 
   updateImagenPerfil(newImageUrl: string) {
     const currentUser = this.getUser();
     currentUser.user.imagenPerfil = newImageUrl;
     sessionStorage.setItem('user', JSON.stringify(currentUser));
 
     this.imagenPerfilSubject.next(newImageUrl);
   }

   canActivate(): boolean {
    const authService = this.injector.get(AuthenticationService);
    if (authService.isAuthenticated()) {
      return true;
    } else {
      this.router.navigate(['/login']);
      return false;
    }
  }

  constructor(
    private injector: Injector,
    private http: HttpClient,
    private router: Router,
  ) {
    super();
  }

  public isAuthenticated(): boolean {
    return !(
      sessionStorage.getItem('token') === undefined ||
      sessionStorage.getItem('token') === null ||
      sessionStorage.getItem('token') === 'null' ||
      sessionStorage.getItem('token') === 'undefined' ||
      sessionStorage.getItem('token') === ''
    );
  }

  public isAuthenticationChanged(): any {
    return this.authenticationChanged.asObservable();
  }

  clearUserData() {
    // Asegura que el socket se cierre antes de limpiar storage
    try {
      this.injector.get(MonitoreoSocketService).desconectar();
    } catch {
      // noop
    }
    this.user = null;
    sessionStorage.clear();
    this.authenticationChanged.next(false);
  }

  public getToken(): any {
    if (
      sessionStorage.getItem("token") === undefined ||
      sessionStorage.getItem("token") === null ||
      sessionStorage.getItem("token") === "null" ||
      sessionStorage.getItem("token") === "undefined" ||
      sessionStorage.getItem("token") === ""
    ) {
      return "";
    }
    return JSON.parse(sessionStorage.getItem("token"));
  }

  public setData(data: User): void {
    if (data?.token) {
      this.setStorageToken(data.token);
    }
    const refreshToken = data?.refreshToken || this.getRefreshToken();
    if (refreshToken) {
      this.setRefreshToken(refreshToken);
    }
    const permisos = this.normalizePermisos(data?.permisos);
    const session = { ...data, permisos };
    this.setStorageUser(session);
    this.setStoragePermissions(permisos);
  }

  public failToken(): void {
    this.cleanSession();
  }

  public logout(): void {
    const refreshToken = this.getRefreshToken();
    const finish = () => this.endSession();
    if (!refreshToken) {
      finish();
      return;
    }
    this.http.post(`${environment.API_SECURITY}/auth/logout`, { refreshToken }).pipe(
      catchError(() => of(null)),
      finalize(() => finish())
    ).subscribe();
  }

  /** Cierra la sesión local cuando el access token ya no se puede renovar. */
  public expireSession(): void {
    this.endSession();
  }

  private endSession(): void {
    if (this.sessionEnding) {
      return;
    }
    this.sessionEnding = true;
    this.cleanSession();
    this.router.navigate(['/login']).finally(() => {
      this.sessionEnding = false;
    });
  }

  private setStorageToken(value: any): void {
    let _value = JSON.stringify(value);
    sessionStorage.setItem("token", _value);
    this.authenticationChanged.next(this.isAuthenticated());
  }

  public changePassword(passwords: CambiarPassword): Observable<boolean> {
		return this.http.put<boolean>(environment.API_SECURITY + '/api/Authentication/CambiarPassword', passwords);
	}

  changePasswordPerfil(passwords: any): Observable<string> {
    return this.http.put(`${environment.API_SECURITY}/usuarios/update/password`, passwords, {
        responseType: 'text'
    }).pipe(
        catchError((err: HttpErrorResponse) => {
            let errorMessage = 'Ocurrió un error al actualizar la contraseña.';
            if (err.status === HttpStatusCode.BadRequest) {
                errorMessage = 'Contraseña actual incorrecta.';
            } else if (err.status === HttpStatusCode.NotFound) {
                errorMessage = 'El recurso solicitado no fue encontrado.';
            } else if (err.status === HttpStatusCode.InternalServerError) {
                errorMessage = 'Ocurrió un error en el servidor.';
            }
            return throwError(errorMessage);
        })
    );
  }

  public solicitarCambioPasswordByEmail(email: string): Observable<any> {
    return this.http.post<any>(`${environment.API_SECURITY}/usuarios/forgot-password`, { email });
  }
  
  public resetPassword(body: any): Observable<string> {
    return this.http.put(`${environment.API_SECURITY}/usuarios/updateForgotPassword`, body, {
        responseType: 'text'
    }).pipe(
        catchError((err: HttpErrorResponse) => {
            let errorMessage = 'Ocurrió un error al actualizar la contraseña.';
            if (err.status === HttpStatusCode.Forbidden) {
                errorMessage = 'No tiene permisos para realizar esta acción.';
            } else if (err.status === HttpStatusCode.NotFound) {
                errorMessage = 'El recurso solicitado no fue encontrado.';
            } else if (err.status === HttpStatusCode.InternalServerError) {
                errorMessage = 'Ocurrió un error en el servidor.';
            }
            return throwError(errorMessage);
        })
    );
  }

  image(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.put(`${environment.API_SECURITY}/usuarios/update/imageProfile`, formData, {
      responseType: 'text'
    });
  }

  actualizarImagenPerfil(data: { imagenPerfil: string }): Observable<any> {
    return this.http.put(`${environment.API_SECURITY}/usuarios/actualizar-imagen-perfil`, data);
  }

  private setStorageUser(value: any): void {
    let _value = JSON.stringify(value);
    sessionStorage.setItem("user", _value);
    this.authenticationChanged.next(this.isAuthenticated());
  }

  public setStorageCoordinate(coordinates): void {
    let coords = JSON.stringify(coordinates);
    sessionStorage.setItem("coordinates", coords);
  }

  updateUsuario(id: string, form: any): Observable<any> {
    return this.http.put<any>(
      `${environment.API_SECURITY}/controlusuarios/${id}`,
      form
    ).pipe(catchError(this.handleError));
  }

  getUsuarioControl(id: string): Observable<any> {
    return this.http.get<any>(
      `${environment.API_SECURITY}/controlusuarios/${id}`
    ).pipe(catchError(this.handleError));
  }

  private setStoragePermissions(permissions: Array<any>): void {
    const transformedPermissions = this.normalizePermisos(permissions).map(permission => permission.idPermiso);
    sessionStorage.setItem("permissions", JSON.stringify(transformedPermissions));
    this.authenticationChanged.next(this.isAuthenticated());
  }

  private normalizePermisos(permissions: Array<any> | null | undefined): Array<{ idPermiso: string }> {
    return (permissions || []).map((permission) => {
      if (permission == null || permission === '') {
        return null;
      }
      if (typeof permission !== 'object') {
        return { idPermiso: String(permission) };
      }
      const id = permission.idPermiso ?? permission.IdPermiso ?? permission.id;
      if (id == null || id === '') {
        return null;
      }
      return { ...permission, idPermiso: String(id) };
    }).filter((permission): permission is { idPermiso: string } => !!permission);
  }

  public cleanSession() {
    // En algunos flujos llaman cleanSession() directo; aseguramos disconnect aquí también.
    try {
      this.injector.get(MonitoreoSocketService).desconectar();
    } catch {
      // noop
    }
    sessionStorage.clear();
  }

  public getUser(): User {
    const user = sessionStorage.getItem("user");
    if (user) {
      return JSON.parse(user);
    } else {
      return new User();
    }
  }

  /**
   * Rol efectivo para jerarquía operativa (Cliente/Cadena/Sucursal/Caja).
   * Usa `user.rol.jerarquiaOperativa` cuando existe; si no, `user.idRol`.
   */
  public getIdRolOperativo(userSession?: User | any): number {
    return getIdRolOperativo(userSession ?? this.getUser());
  }

  public getIdRolOperativoString(userSession?: User | any): string {
    return getIdRolOperativoString(userSession ?? this.getUser());
  }

  public getCoordinates() {
    return JSON.parse(sessionStorage.getItem("coordinates"));
  }

  public getPermissions(): string[] {
    return JSON.parse(sessionStorage.getItem("permissions"));
  }

  public authenticate(body: Credentials): Observable<User> {
    return this.http.post<AuthTokens>(`${environment.API_SECURITY}/auth/login`, {
      username: body.username,
      password: body.password
    }).pipe(
      switchMap((tokens) => {
        this.persistTokens(tokens);
        return this.http.get<any>(`${environment.API_SECURITY}/auth/me`).pipe(
          map((me) => this.toSession(tokens, me))
        );
      }),
      catchError((error) => {
        this.cleanSession();
        return throwError(() => error);
      })
    );
  }

  public refreshSession(): Observable<AuthTokens> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No hay refresh token'));
    }
    this.refreshInFlight = this.http.post<AuthTokens>(`${environment.API_SECURITY}/auth/refresh`, { refreshToken }).pipe(
      tap((tokens) => this.persistTokens(tokens)),
      finalize(() => {
        this.refreshInFlight = null;
      }),
      shareReplay(1)
    );
    return this.refreshInFlight;
  }

  public getRefreshToken(): string {
    return this.readStorageString('refreshToken');
  }

  private persistTokens(tokens: AuthTokens): void {
    this.setStorageToken(tokens?.token || '');
    this.setRefreshToken(tokens?.refreshToken || '');
    const raw = sessionStorage.getItem('user');
    if (!raw) {
      return;
    }
    try {
      const current = JSON.parse(raw);
      current.token = tokens.token;
      current.refreshToken = tokens.refreshToken;
      sessionStorage.setItem('user', JSON.stringify(current));
    } catch {
      // la sesión de usuario se arma después del login
    }
  }

  private setRefreshToken(value: string): void {
    sessionStorage.setItem('refreshToken', JSON.stringify(value || ''));
  }

  private readStorageString(key: string): string {
    const raw = sessionStorage.getItem(key);
    if (!raw || raw === 'null' || raw === 'undefined') {
      return '';
    }
    try {
      const parsed = JSON.parse(raw);
      return parsed == null ? '' : String(parsed);
    } catch {
      return raw;
    }
  }

  private toSession(tokens: AuthTokens, me: any): User {
    const payload = me?.data ?? me ?? {};
    const userSource = payload.user ?? payload.usuario ?? payload;
    const userInfo = { ...(userSource || {}) };
    this.attachNamedRelation(userInfo, payload, 'cliente', ['nombreCliente', 'clienteNombre']);
    this.attachNamedRelation(userInfo, payload, 'cadena', ['nombreCadena', 'cadenaNombre']);
    this.attachNamedRelation(userInfo, payload, 'sucursal', ['nombreSucursal', 'sucursalNombre']);
    this.attachNamedRelation(userInfo, payload, 'caja', ['nombreCaja', 'cajaNombre']);

    const permisos = this.normalizePermisos(
      payload.permisos ?? payload.permissions ?? userInfo.permisos ?? []
    );

    return {
      token: tokens.token,
      refreshToken: tokens.refreshToken,
      user: userInfo,
      permisos
    } as User;
  }

  private attachNamedRelation(userInfo: any, payload: any, key: string, aliases: string[]): void {
    const aliasValue = aliases.map((alias) => payload[alias]).find((value) => value != null);
    const incoming = payload[key] ?? aliasValue;
    const current = userInfo[key];
    if ((incoming == null || incoming === '') && (current == null || current === '')) {
      return;
    }
    if (typeof incoming === 'string' || typeof incoming === 'number') {
      userInfo[key] = {
        ...(current && typeof current === 'object' ? current : {}),
        nombre: String(incoming)
      };
      return;
    }
    if (incoming && typeof incoming === 'object') {
      userInfo[key] = {
        ...(current && typeof current === 'object' ? current : {}),
        ...incoming
      };
      return;
    }
    if (typeof current === 'string' || typeof current === 'number') {
      userInfo[key] = { nombre: String(current) };
    }
  }
}

interface AuthTokens {
  token: string;
  refreshToken: string;
}
